import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import { mixpanel } from "common/analytics";
import { useTranslation } from "common/locales";
import EnterReseedEvents from "features/bulkReseedEventInput/EnterReseedEvents";
import EnterFeedEvent from "features/feedPurchaseDetail/EnterFeedEvent";
import EnterFeedPurchaseEvent from "features/feedPurchaseDetail/EnterFeedPurchaseEvent";
import { checkConsistency } from "features/feedPurchaseDetail/FeedPurchaseEvents";
import EnterFertilizerEvent from "features/fertilizerInput/EnterFertilizerEvent";
import {
  convertFromFormInput,
  FertilizerEventFormData,
} from "features/fertilizerInput/FertilizerEventFormData";
import EnterFieldPreparationEvent from "features/fieldPreparationInput/EnterFieldPreparationEvent";
import {
  convertFromFormInput as convertFromFieldPrepFormInput,
  FieldPreparationEventFormData,
} from "features/fieldPreparationInput/FieldPreparationEventFormData";
import { NameAndGuid } from "features/general/NameAndGuidMultiSelectDropdown";
import { StyledInputContainer } from "features/StyledInputContainer";
import EnterYieldEvent from "features/yieldInput/EnterYieldEvent";
import {
  createFeedEvent,
  createFeedPurchaseEvent,
  createFertilizerEvent,
  createFieldPreparationEvent,
  createYieldEvent,
  GeneralFeedEvent,
  GENERAL_FEED_TYPE,
  GENERAL_FEED_UNITS,
  MACHINERY,
  PURCHASE_CURRENCY,
  RESEED_TOTAL_AMOUNT_UNIT,
  RESEED_TYPE,
  SEEDING_METHOD,
  selectCurrentFarm,
  useAppDispatch,
  useAppSelector,
  YieldEvent,
} from "model";
import { fetchFieldsById, GRASS_TYPE } from "model/fieldSlice";
import {
  createReseedsEventForFarm,
  getAllReseedEventsForFarm,
} from "model/reseedEventSlice";
import { RootState } from "model/rootReducer";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

export function AddReseedEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const currentFarmId = useSelector(
    (state: RootState) => state.farms.currentFarmId
  );

  const handleClose = () => {
    setDialogOpen(false);
    mixpanel.track("Enter re-seed event cancelled");
  };

  const [newGrassType, setNewGrassType] = useState<GRASS_TYPE | undefined>(
    undefined
  );
  const [grassTypeError, setGrassTypeError] = useState<boolean>(false);
  const [seedingMethod, setSeedingMethod] = useState<
    SEEDING_METHOD | undefined
  >(undefined);
  const [seedingMethodError, setSeedingMethodError] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const [selectedFields, setSelectedFields] = useState<NameAndGuid[]>([]);
  const [reseedDate, setReseedDate] = useState<Date | null>(new Date());
  const [reseedDateError, setReseedDateError] = useState<boolean>(false);
  const [machinery, setMachinery] = useState<MACHINERY[]>([]);
  const [fuel, setFuel] = useState<number>("" as unknown as number);
  const [operationalCost, setOperationalCost] = useState<number>(
    "" as unknown as number
  );
  const [productCost, setProductCost] = useState<number>(
    "" as unknown as number
  );
  const [reseedType, setReseedType] = useState<RESEED_TYPE>(RESEED_TYPE.RESEED);
  const [seedingRate, setSeedingRate] = useState<number>(
    "" as unknown as number
  );
  const [productName, setProductName] = useState<string>("");
  const [totalAmountUnit, setTotalAmountUnit] =
    useState<RESEED_TOTAL_AMOUNT_UNIT>(
      "" as unknown as RESEED_TOTAL_AMOUNT_UNIT
    );
  const [totalAmount, setTotalAmount] = useState<number>(
    "" as unknown as number
  );
  const [fieldsSelectInputError, setFieldsSelectInputError] =
    useState<boolean>(false);

  async function handleSave() {
    if (!newGrassType) {
      setGrassTypeError(true);
    }
    if (!reseedDate) {
      setReseedDateError(true);
    }
    if (selectedFields.length === 0) {
      setFieldsSelectInputError(true);
    }
    if (!seedingMethod) {
      setSeedingMethodError(true);
    }

    if (
      !newGrassType ||
      !reseedDate ||
      !seedingMethod ||
      selectedFields.length === 0 ||
      grassTypeError ||
      reseedDateError ||
      fieldsSelectInputError ||
      seedingMethodError
    ) {
      return;
    }

    const fieldGuids = selectedFields.map(
      (selectedField: NameAndGuid) => selectedField.guid ?? ""
    );

    const totalAmountUnitForPost =
      totalAmountUnit === ("" as unknown as RESEED_TOTAL_AMOUNT_UNIT)
        ? undefined
        : totalAmountUnit;

    const finalOperationalCost =
      operationalCost === ("" as unknown as number)
        ? undefined
        : operationalCost;

    const finalProductCost =
      productCost === ("" as unknown as number) ? undefined : productCost;

    await createReseedsEventForFarm({
      note: notes,
      reseedDate: reseedDate.toISOString(),
      newGrassType: newGrassType,
      seedingMethod: seedingMethod,
      machinery: machinery,
      fuel: fuel,
      operationalCost: finalOperationalCost,
      productCost: finalProductCost,
      operationalCostUnit: finalOperationalCost
        ? PURCHASE_CURRENCY.GBP
        : undefined,
      productCostUnit: finalProductCost ? PURCHASE_CURRENCY.GBP : undefined,
      fieldGuids,
      reseedType: reseedType,
      productName: productName,
      seedingRate: seedingRate,
      totalAmount: totalAmount ? totalAmount : undefined,
      totalAmountUnit: totalAmount ? totalAmountUnitForPost : undefined,
    });
    await dispatch(getAllReseedEventsForFarm({ farmId: currentFarmId ?? "" }));
    await dispatch(fetchFieldsById(fieldGuids));
    notify(t("updateGrassTypes.success"));
    mixpanel.track("Save reseed events successful");
    setDialogOpen(false);
    history.push("/inputs-and-outputs");
  }

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      disableEscapeKeyDown
      open={dialogOpen}
      onClose={handleClose}
    >
      <StyledInputContainer isMobile={isMobile}>
        <EnterReseedEvents
          newGrassType={newGrassType}
          selectedFields={selectedFields}
          setSelectedFields={setSelectedFields}
          setNewGrassType={setNewGrassType}
          handleSave={handleSave}
          setNotes={setNotes}
          notes={notes}
          reseedDate={reseedDate}
          setReseedDate={setReseedDate}
          grassTypeError={grassTypeError}
          reseedDateError={reseedDateError}
          setGrassTypeError={setGrassTypeError}
          setSeedingMethod={setSeedingMethod}
          seedingMethodError={seedingMethodError}
          setSeedingMethodError={setSeedingMethodError}
          setReseedDateError={setReseedDateError}
          fieldsSelectInputError={fieldsSelectInputError}
          setFieldsSelectInputError={setFieldsSelectInputError}
          seedingMethod={seedingMethod}
          machinery={machinery}
          setMachinery={setMachinery}
          fuel={fuel}
          setFuel={setFuel}
          enableFieldSelection={true}
          onCancel={handleClose}
          reseedType={reseedType}
          setReseedType={setReseedType}
          productName={productName}
          setProductName={setProductName}
          seedingRate={seedingRate}
          setSeedingRate={setSeedingRate}
          totalAmountUnit={totalAmountUnit}
          setTotalAmountUnit={setTotalAmountUnit}
          totalAmount={totalAmount}
          setTotalAmount={setTotalAmount}
          operationalCost={operationalCost}
          setOperationalCost={setOperationalCost}
          productCost={productCost}
          setProductCost={setProductCost}
        />
      </StyledInputContainer>
    </Dialog>
  );
}

export function AddFeedEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const dispatch = useAppDispatch();
  const currentFarm = useAppSelector(selectCurrentFarm);
  const { notify } = useNotification();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  const handleClose = () => {
    setDialogOpen(false);
    mixpanel.track("Create feed event cancelled");
  };

  async function handleSave(generalFeedEvent: GeneralFeedEvent) {
    await dispatch(
      createFeedEvent({
        generalFeedEvent,
        farmGuid: currentFarm?.guid ?? "",
      })
    );
    notify(t("addFeedEvent.add.success"));
    mixpanel.track("FeedEvent created");
    setDialogOpen(false);
    history.push("/inputs-and-outputs");
  }

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      disableEscapeKeyDown
      open={dialogOpen}
      onClose={handleClose}
    >
      <StyledInputContainer isMobile={isMobile}>
        <EnterFeedEvent
          feedEvent={undefined}
          handleSave={handleSave}
          handleClose={handleClose}
        />
      </StyledInputContainer>
    </Dialog>
  );
}

export function AddFeedPurchaseEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const currentFarmId = useSelector(
    (state: RootState) => state.farms.currentFarmId
  );

  const handleClose = () => {
    setDialogOpen(false);
    mixpanel.track("Create feed purchase events cancelled");
  };

  const [feedType, setFeedType] = useState<GENERAL_FEED_TYPE | undefined>(
    undefined
  );
  const [feedTypeError, setFeedTypeError] = useState<boolean>(false);
  const [feedUnit, setFeedUnit] = useState<GENERAL_FEED_UNITS | undefined>(
    undefined
  );
  const [feedUnitError, setFeedUnitError] = useState<boolean>(false);
  const [notes, setNotes] = useState<string | undefined>(undefined);
  const [cost, setCost] = useState<string | undefined>();
  const [costError, setCostError] = useState<boolean>(false);
  const [feedAmount, setFeedAmount] = useState<string | undefined>();
  const [feedAmountError, setFeedAmountError] = useState<boolean>(false);
  const [date, setDate] = useState<Date | null>(new Date());
  const [dateError, setDateError] = useState<boolean>(false);

  async function createfeedPurchaseEvents() {
    if (!feedType || !date || !currentFarmId || !feedAmount) {
      return;
    }

    return await dispatch(
      createFeedPurchaseEvent({
        farmGuid: currentFarmId,
        guid: " ",
        note: notes ?? "",
        feedPurchaseEventDate: date.toISOString(),
        feedPurchases: [
          {
            costCurrency:
              cost === undefined ? undefined : PURCHASE_CURRENCY.GBP,
            cost: cost ? Number(cost) : undefined,
            unitType: feedUnit ?? GENERAL_FEED_UNITS.TONNE,
            feedType: feedType,
            amount: Number(feedAmount),
          },
        ],
      })
    );
  }

  async function handleSave() {
    const consistent = checkConsistency(
      feedType,
      setFeedTypeError,
      feedUnit,
      setFeedUnitError,
      date,
      setDateError,
      dateError,
      feedAmount,
      setFeedAmountError,
      cost,
      setCostError
    );
    if (!consistent) {
      return;
    }
    await createfeedPurchaseEvents();

    notify(t("createfeedPurchaseEvent.success"));
    mixpanel.track("Create feed purchase events successful");
    setDialogOpen(false);
    history.push("/inputs-and-outputs");
  }

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      disableEscapeKeyDown
      open={dialogOpen}
      onClose={handleClose}
    >
      <StyledInputContainer isMobile={isMobile}>
        <EnterFeedPurchaseEvent
          feedType={feedType}
          cost={cost}
          setCost={setCost}
          setFeedType={setFeedType}
          handleSave={handleSave}
          setNotes={setNotes}
          note={notes}
          date={date}
          setDate={setDate}
          feedTypeError={feedTypeError}
          dateError={dateError}
          setFeedTypeError={setFeedTypeError}
          setFeedUnit={setFeedUnit}
          feedUnitError={feedUnitError}
          setFeedUnitError={setFeedUnitError}
          feedUnit={feedUnit}
          feedAmount={feedAmount}
          setFeedAmount={setFeedAmount}
          feedAmountError={feedAmountError}
          setFeedAmountError={setFeedAmountError}
          costError={costError}
          setCostError={setCostError}
          handleClose={handleClose}
        />
      </StyledInputContainer>
    </Dialog>
  );
}

export function AddYieldEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const dispatch = useAppDispatch();
  const currentFarm = useAppSelector(selectCurrentFarm);
  const { notify } = useNotification();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  const handleClose = () => {
    setDialogOpen(false);
    mixpanel.track("Create yield event cancelled");
  };

  async function handleSave(yieldEvent: YieldEvent) {
    await dispatch(
      createYieldEvent({
        yieldEvent,
        farmGuid: currentFarm?.guid ?? "",
      })
    );
    notify(t("addYieldEvent.add.success"));
    mixpanel.track("YieldEvent created");
    setDialogOpen(false);
    history.push("/inputs-and-outputs");
  }

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      disableEscapeKeyDown
      open={dialogOpen}
      onClose={handleClose}
    >
      <StyledInputContainer isMobile={isMobile}>
        <EnterYieldEvent
          yieldEvent={undefined}
          handleSave={handleSave}
          handleClose={handleClose}
        />
      </StyledInputContainer>
    </Dialog>
  );
}

export function AddFertilizerEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  const handleClose = () => {
    setDialogOpen(false);
    mixpanel.track("Create fertilizer event cancelled");
  };

  async function handleSave(
    fertilizerEventFormData: FertilizerEventFormData,
    farmGuid: string | undefined
  ) {
    const fertilizerEventBody = convertFromFormInput(fertilizerEventFormData);
    farmGuid = farmGuid ?? "";
    await dispatch(
      createFertilizerEvent({
        fertilizerEvent: fertilizerEventBody,
        farmGuid,
      })
    );

    notify(t("addFertilizerEvent.add.success"));
    mixpanel.track("FertilizerEvent created");
    setDialogOpen(false);
    history.push("/inputs-and-outputs");
  }

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      disableEscapeKeyDown
      open={dialogOpen}
      onClose={handleClose}
    >
      <StyledInputContainer isMobile={isMobile}>
        <EnterFertilizerEvent
          fertilizerEvent={undefined}
          handleSave={handleSave}
          handleClose={handleClose}
          fieldSelectDisabled={false}
        />
      </StyledInputContainer>
    </Dialog>
  );
}

export function AddFieldPreparationEventDialog({
  dialogOpen,
  setDialogOpen,
}: {
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
}) {
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  const handleClose = () => {
    setDialogOpen(false);
    mixpanel.track("Create field preparation event cancelled");
  };

  async function handleSave(
    fieldPreparationEventFormData: FieldPreparationEventFormData
  ) {
    const fieldPreparationEvent = convertFromFieldPrepFormInput(
      fieldPreparationEventFormData
    );
    await dispatch(createFieldPreparationEvent(fieldPreparationEvent));

    notify(t("addFieldPreparationEvent.add.success"));
    mixpanel.track("FieldPreparationEvent created");
    setDialogOpen(false);
    history.push("/inputs-and-outputs");
  }

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      disableEscapeKeyDown
      open={dialogOpen}
      onClose={handleClose}
    >
      <StyledInputContainer isMobile={isMobile}>
        <EnterFieldPreparationEvent
          fieldPreparationEvent={undefined}
          handleSave={handleSave}
          handleClose={handleClose}
          fieldSelectDisabled={false}
        />
      </StyledInputContainer>
    </Dialog>
  );
}
